import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { blockContextOf } from './blockContext';
import { readPasteable, writeClipboard } from './clipboard';
import { buildInlineIndex } from './inlineMap';
import { buildTableIndex } from './tableMap';
import { isSharedBlock, markerId } from './types';
import type { BlockNode } from './types';
import type { BuilderSurface, RenderService } from './host';
import type { BuilderStore } from './useBuilderStore';
import type { BuilderBridge } from './useBuilderBridge';
import type { BuilderRegistry } from './useBuilderRegistry';
import type { PanelManager } from './usePanelManager';

/**
 * Shared shell behavior of the full builder and Canvas-lite: registry-driven
 * bridge index (labels + inline map), the hybrid canvas patch loop and the
 * window shortcut subset. Draft/autosave stays outside — the shells differ
 * exactly there.
 */
export interface BuilderShell {
    paletteOpen: Ref<boolean>;
    keymapOpen: Ref<boolean>;
    /** Bumped when the canvas must fully reload instead of patching. */
    reloadSerial: Ref<number>;
    patchStructure(): Promise<void>;
}

/** Hybrid canvas update (spec §5.5): value edits patch one block subtree
 *  (400 ms debounce), structural changes swap the content region. */
const VALUE_PATCH_DEBOUNCE_MS = 400;

export interface BuilderShellOptions {
    /** Floating panel manager (full builder only) — extends the Esc chain. */
    panels?: PanelManager;
    /** Active editing surface; 'document' silences the canvas patch loop. */
    surface?: Ref<BuilderSurface>;
}

export function useBuilderShell(
    store: BuilderStore,
    bridge: BuilderBridge,
    registry: BuilderRegistry,
    render: RenderService,
    options: BuilderShellOptions = {},
): BuilderShell {
    /** Outline labels + canvas hover chips, resolved from the registry. */
    function blockLabel(block: BlockNode): string {
        if (isSharedBlock(block)) {
            return registry.blockTitle(String(block.type)) ?? String(block.type);
        }

        return registry.typeOf(block.type)?.name ?? String(block.type);
    }

    function rebuildBridgeIndex(): void {
        const labels: Record<string, string> = {};

        const visit = (blocks: BlockNode[]): void => {
            for (const block of blocks) {
                const id = markerId(block);

                if (id !== null) {
                    labels[id] = blockLabel(block);
                }

                if (block.children?.length) {
                    visit(block.children);
                }
            }
        };

        visit(store.content.value);

        // Equality skip: the deep content watch fires on every streamed inline
        // commit — an unchanged index must not re-send init to the bridge.
        const inline = buildInlineIndex(store.content.value, registry.types.value);
        const tables = buildTableIndex(store.content.value, registry.types.value);

        if (JSON.stringify(labels) !== JSON.stringify(store.labels.value)) {
            store.labels.value = labels;
        }

        if (JSON.stringify(inline) !== JSON.stringify(store.inline.value)) {
            store.inline.value = inline;
        }

        if (JSON.stringify(tables) !== JSON.stringify(store.tables.value)) {
            store.tables.value = tables;
        }
    }

    onMounted(async () => {
        await registry.load();
        rebuildBridgeIndex();
    });

    watch(() => store.content.value, rebuildBridgeIndex, { deep: true });

    let valueTimer: ReturnType<typeof setTimeout> | null = null;

    async function patchValue(id: string): Promise<void> {
        const block = store.findBlock(id);

        if (!block) {
            return patchStructure();
        }

        const { html } = await render.renderLevel(block);
        bridge.patchLevel(id, html);
    }

    async function patchStructure(): Promise<void> {
        const { html } = await render.renderTree(store.content.value);
        bridge.patchRegion(html);
    }

    const reloadSerial = ref(0);

    /** Reload-only types (Base::reloadOnly) opt out of in-place patching —
     *  their scripts do not survive a DOM swap. */
    function blockIsReloadOnly(block: BlockNode): boolean {
        return !isSharedBlock(block) && registry.typeOf(block.type)?.reloadOnly === true;
    }

    function treeHasReloadOnly(blocks: BlockNode[]): boolean {
        for (const block of blocks) {
            if (blockIsReloadOnly(block)) {
                return true;
            }

            if (block.children?.length && treeHasReloadOnly(block.children)) {
                return true;
            }
        }

        return false;
    }

    watch(store.lastMutation, (mutation) => {
        if (mutation === null) {
            return;
        }

        // Inline edits already mutated the iframe DOM — no patch, the block is
        // dirty and the next structural render reconciles it (spec §5.6).
        if (mutation.origin === 'inline') {
            return;
        }

        // Document surface active (or a stray document-origin edit): there is
        // no iframe to patch — switching back to canvas reloads it instead.
        if (mutation.origin === 'document' || options.surface?.value === 'document') {
            return;
        }

        if (mutation.kind === 'value' && mutation.id !== null) {
            const block = store.findBlock(mutation.id);

            if (block && blockIsReloadOnly(block)) {
                reloadSerial.value += 1;
                return;
            }

            if (valueTimer) {
                clearTimeout(valueTimer);
            }

            const id = mutation.id;
            valueTimer = setTimeout(() => void patchValue(id), VALUE_PATCH_DEBOUNCE_MS);

            return;
        }

        // A region swap would tear down every reload-only block's DOM state
        // anyway — a full reload re-runs the theme scripts in proper order.
        if (treeHasReloadOnly(store.content.value)) {
            reloadSerial.value += 1;
            return;
        }

        void patchStructure();
    });

    // Failed patch or a captured theme-script error → the canvas DOM can no
    // longer be trusted; reload instead of patching a broken document. The
    // cooldown breaks reload→error→reload loops on persistently broken JS.
    const AUTO_RELOAD_COOLDOWN_MS = 5_000;
    let lastAutoReloadAt = 0;

    watch(bridge.patchIssueSerial, () => {
        const now = Date.now();

        if (now - lastAutoReloadAt < AUTO_RELOAD_COOLDOWN_MS) {
            return;
        }

        lastAutoReloadAt = now;
        reloadSerial.value += 1;
    });

    const paletteOpen = ref(false);
    const keymapOpen = ref(false);

    // Cmd+K / `?` pressed inside the iframe cannot reach this listener — the
    // bridge forwards them as shortcut messages instead.
    watch(bridge.paletteShortcutSerial, () => {
        paletteOpen.value = true;
    });

    watch(bridge.keymapShortcutSerial, () => {
        keymapOpen.value = true;
    });

    /** Selected blocks without a selected ancestor — Cmd+C payload. */
    function copySelection(): void {
        const idSet = new Set(store.selectedIds.value);
        const isTopLevel = (id: string): boolean => {
            let parentId = blockContextOf(store.content.value, id)?.parentId ?? null;

            while (parentId !== null) {
                if (idSet.has(parentId)) {
                    return false;
                }

                parentId = blockContextOf(store.content.value, parentId)?.parentId ?? null;
            }

            return true;
        };

        const blocks = store.selectedIds.value
            .filter(isTopLevel)
            .map((id) => store.findBlock(id))
            .filter((block): block is NonNullable<typeof block> => block !== null);

        if (blocks.length) {
            writeClipboard(blocks);
        }
    }

    /** Cmd+V: paste after the primary selection (root end without one). */
    function pasteAfterSelection(): boolean {
        const context = store.selectedId.value === null ? null : blockContextOf(store.content.value, store.selectedId.value);
        const parent = context?.parentId ? store.findBlock(context.parentId) : null;
        const blocks = readPasteable(parent, (slug) => registry.typeOf(slug) !== null);

        if (blocks === null) {
            return false;
        }

        return store.pasteAt(blocks, context?.parentId ?? null, context?.slot ?? 0, context?.nextId ?? null).length > 0;
    }

    /** Cmd+Z / Shift+Cmd+Z / Del / Cmd+D / Cmd+C / Cmd+V / Cmd+K / ? — the shell shortcut subset. */
    function onKeydown(event: KeyboardEvent): void {
        const target = event.target as HTMLElement | null;

        // Cmd+K works even while typing in the panel — before the input guard
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            paletteOpen.value = !paletteOpen.value;
            return;
        }

        if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
            return;
        }

        const meta = event.metaKey || event.ctrlKey;

        if (event.key === '?' && !meta) {
            event.preventDefault();
            keymapOpen.value = !keymapOpen.value;
        } else if (meta && event.key.toLowerCase() === 'z') {
            event.preventDefault();

            if (event.shiftKey) {
                store.redo();
            } else {
                store.undo();
            }
        } else if (meta && event.key.toLowerCase() === 'd' && store.selectedIds.value.length) {
            event.preventDefault();
            store.duplicateBlocks([...store.selectedIds.value]);
        } else if (meta && event.key.toLowerCase() === 'c' && store.selectedIds.value.length) {
            // Text selections keep the native copy behaviour.
            if (window.getSelection()?.isCollapsed !== false) {
                event.preventDefault();
                copySelection();
            }
        } else if (meta && event.key.toLowerCase() === 'v') {
            if (pasteAfterSelection()) {
                event.preventDefault();
            }
        } else if ((event.key === 'Delete' || event.key === 'Backspace') && store.selectedIds.value.length) {
            event.preventDefault();
            store.removeBlocks([...store.selectedIds.value]);
        } else if (event.key === 'Escape') {
            // Esc chain (F2): the overlays close themselves (their focus sits
            // in the dialog), then the top floating panel, then deselect.
            if (paletteOpen.value || keymapOpen.value) {
                return;
            }

            if (options.panels?.closeTop()) {
                return;
            }

            store.select(null);
        }
    }

    onMounted(() => window.addEventListener('keydown', onKeydown));
    onBeforeUnmount(() => {
        window.removeEventListener('keydown', onKeydown);

        if (valueTimer) {
            clearTimeout(valueTimer);
        }
    });

    return { paletteOpen, keymapOpen, reloadSerial, patchStructure };
}
